import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, OnInit, Output, signal, WritableSignal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { CommonService } from '@appShared/services'
import {
   Countries,
   Country,
   Country_,
   StateProvince,
   StateProvinces
} from '@appShared/services/lookup/[CodeGen]'

@Component({
   selector: 'app-billing-address',
   templateUrl: './billing-address.component.html',
   styleUrls: ['./billing-address.component.less'],
   changeDetection: ChangeDetectionStrategy.OnPush,
   imports: [ReactiveFormsModule]
})
export class BillingAddressComponent implements OnInit {
   @Output() formReady = new EventEmitter<FormGroup>()

   private readonly _fb = inject(FormBuilder)
   private readonly _commonService = inject(CommonService)
   private readonly _destroyRef = inject(DestroyRef)
   private readonly _regexp = this._commonService.regexp()

   public readonly countries: Country[] = Countries
      .filter(country => country.code)
      .sort((a, b) => (a.listPriority - b.listPriority) || a.name.localeCompare(b.name))

   public statesForCountry: WritableSignal<StateProvince[]> = signal<StateProvince[]>([])

   public form!: FormGroup

   ngOnInit(): void {
      this.form = this._fb.group({
         line1: ['', [Validators.required, Validators.maxLength(120)]],
         line2: ['', [Validators.maxLength(120)]],
         city: ['', [Validators.required, Validators.maxLength(80)]],
         countryCode: [Country_.US, [Validators.required]],
         stateProvinceCode: [null, [Validators.required]],
         postalCode: ['', [Validators.required, Validators.maxLength(20)]]
      })

      this._refreshStatesForCountry(this.form.get('countryCode')!.value as Country_)
      this._refreshPostalCodeValidator(this.form.get('countryCode')!.value as Country_)

      this.form.get('countryCode')!.valueChanges
         .pipe(takeUntilDestroyed(this._destroyRef))
         .subscribe((countryCode: Country_) => {
            this._refreshStatesForCountry(countryCode)
            this._refreshPostalCodeValidator(countryCode)

            // clear previously-selected state and postal code so they don't carry across countries
            this.form.get('stateProvinceCode')!.setValue(null)
            this.form.get('postalCode')!.setValue('')
            this.form.get('postalCode')!.markAsUntouched()
         })

      this.formReady.emit(this.form)
   }

   /*
   * private methods
   * */

   private _refreshStatesForCountry(countryCode: Country_): void {
      const states = StateProvinces
         .filter(province => province.countryCode === countryCode)
         .sort((a, b) => (a.listPriority - b.listPriority) || a.name.localeCompare(b.name))

      this.statesForCountry.set(states)

      const stateCtrl = this.form.get('stateProvinceCode')!
      if (states.length === 0) {
         stateCtrl.disable({ emitEvent: false })
         stateCtrl.clearValidators()
      } else {
         stateCtrl.enable({ emitEvent: false })
         stateCtrl.setValidators([Validators.required])
      }
      stateCtrl.updateValueAndValidity({ emitEvent: false })
   }

   private _refreshPostalCodeValidator(countryCode: Country_): void {
      const postalCtrl = this.form.get('postalCode')!
      const validators = [Validators.required, Validators.maxLength(20)]

      if (countryCode === Country_.US) {
         validators.push(Validators.pattern(this._regexp.postalCode.usOnly))
      } else if (countryCode === Country_.CA) {
         validators.push(Validators.pattern(this._regexp.postalCode.caOnly))
      }

      postalCtrl.setValidators(validators)
      postalCtrl.updateValueAndValidity({ emitEvent: false })
   }
}
